/**
 * DashboardRoleView - Composants personnalisés par rôle
 * 
 * RÈGLES DE VISIBILITÉ:
 * - Admin Full: Vue globale (toutes équipes, tous utilisateurs, stats complètes)
 * - Admin: Vue globale simplifiée
 * - VP Superviseur: Ses équipes + ses VP + apporteurs de l'équipe
 * - VP Payeur: Son équipe + ses apporteurs + ses stats
 * - Apporteur: Ses propres stats uniquement
 * - Comptable: Vue financière
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
    Users, Crown, Target, TrendingUp, Award, Star, 
    Briefcase, Euro, Calendar, Clock, CheckCircle2,
    ArrowRight, Zap, HardHat, Trophy, Medal, Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
// PrimeSecteurWidget retiré de ce fichier : affiché une seule fois via Dashboard.jsx

// Formater les montants
const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value || 0);
};

// Stages "gagnés" — RÈGLE MÉTIER UNIQUE, alignée sur le backend (models/base.py
// EP_WON_STAGES / CH_WON_STAGES) pour que apporteur, VP et admin partagent la même
// définition. "Travaux d’optimisation terminé" = gagné. "À aller chercher" = en cours avec commission connue.
const VALIDATED_EP_STAGES = ['ep_gagne', 'ep_questionnaire_chantier'];
const VALIDATED_CH_STAGES = ['ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue', 'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours', 'ch_termine', 'ch_chantier_termine', 'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide', 'ch_gagne'];

// Helper: filtrer les prospects "gagnés" selon la période sélectionnée
const filterWonByPeriod = (prospects, advancedFilters) => {
    const periodStart = advancedFilters?.dateFrom || null;
    const periodEnd = advancedFilters?.dateTo || null;
    const hasPeriodFilter = !!(periodStart && periodEnd);
    
    return prospects.filter(p => {
        const isWonEP = VALIDATED_EP_STAGES.includes(p.easy_power_stage);
        const isWonCH = VALIDATED_CH_STAGES.includes(p.chantier_stage);
        if (!isWonEP && !isWonCH) return false;
        if (!hasPeriodFilter) return true;
        
        const inPeriod = (dateStr) => {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            return d >= periodStart && d <= periodEnd;
        };
        
        return (isWonEP && inPeriod(p.easy_power_stage_changed_at)) || 
               (isWonCH && inPeriod(p.chantier_stage_changed_at));
    });
};

// Helper: label de la période
const getPeriodLabel = (advancedFilters) => {
    const type = advancedFilters?.periodType;
    if (!type || type === 'all') return 'toutes périodes';
    if (type === 'month') return 'ce mois';
    if (type === 'week') return 'cette semaine';
    if (type === 'quarter') return 'ce trimestre';
    if (type === 'year') return 'cette année';
    if (type === 'custom') return 'période personnalisée';
    return 'toutes périodes';
};

// Helper: ce dossier est-il "gagné" (EP ou CH validé) ?
const isWonDossier = (p) => VALIDATED_EP_STAGES.includes(p.easy_power_stage) || VALIDATED_CH_STAGES.includes(p.chantier_stage);

// Cellule "Ma commission" PAR MODULE (Courtage / Travaux d’optimisation) — un dossier peut porter les
// deux à des stades différents. Objectif = MOTIVER :
//  - non renseignée        → "Faites avancer" (bleu) : pousse à faire progresser le dossier
//  - renseignée + pas gagné → "À aller chercher" (ambre) + montant : pousse à closer
//  - renseignée + gagné     → "Com. gagnée" (vert) + montant : satisfaction
function ModuleCommission({ entry }) {
    const filled = typeof entry.amount === 'number' && entry.amount > 0;
    const tag = entry.key === 'ep' ? 'EP' : 'CH';
    let state, cls, Icon;
    if (!filled) { state = 'Faites avancer'; cls = 'border-blue-200 bg-blue-50 text-blue-600'; Icon = TrendingUp; }
    else if (entry.won) { state = 'Com. gagnée'; cls = 'border-emerald-200 bg-emerald-50 text-emerald-700'; Icon = Trophy; }
    else { state = 'À aller chercher'; cls = 'border-amber-200 bg-amber-50 text-amber-700'; Icon = Target; }
    return (
        <div className={cn('flex items-center gap-1.5 rounded-lg border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap', cls)}
            data-testid="apporteur-commission-cell">
            <span className="text-[9px] font-bold opacity-70">{tag}</span>
            <Icon className="w-3 h-3 shrink-0" />
            <span className="hidden sm:inline">{state}</span>
            {filled ? <span className="font-bold">{formatCurrency(entry.amount)}</span> : <span className="opacity-70">à venir</span>}
        </div>
    );
}

export function CommissionCells({ entries }) {
    if (!entries || entries.length === 0) return null;
    return (
        <div className="flex flex-col items-end gap-1 shrink-0">
            {entries.map((e) => <ModuleCommission key={e.key} entry={e} />)}
        </div>
    );
}

// Construit les entrées de commission par module actif d'un dossier pour un apporteur donné.
// Réutilisable hors composant (ex. widget "Dossiers à relancer").
export function makeModuleCommissions(p, userId) {
    const isApp2 = p.dossier_apporteur_2_id === userId;
    const out = [];
    if (p.easy_power_stage) {
        out.push({
            key: 'ep', label: 'Courtage',
            amount: isApp2 ? (p.ep_commission_apporteur_2 || 0) : (p.ep_commission_apporteur_1 || p.ep_commission_apporteur || 0),
            won: VALIDATED_EP_STAGES.includes(p.easy_power_stage),
        });
    }
    if (p.chantier_stage) {
        out.push({
            key: 'ch', label: 'Travaux d’optimisation',
            amount: isApp2 ? (p.ch_commission_apporteur_2 || 0) : (p.ch_commission_apporteur_1 || p.ch_commission_apporteur || 0),
            won: VALIDATED_CH_STAGES.includes(p.chantier_stage),
        });
    }
    return out;
}


// ============================================================
// VUE APPORTEUR - Mes performances personnelles
// ============================================================
export function ApporteurDashboardView({ user, prospects, commissions, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Stats personnelles
    const myProspects = prospects.filter(p => p.assigned_to === user?.id || p.dossier_apporteur_id === user?.id);
    const myWonProspects = filterWonByPeriod(myProspects, advancedFilters);
    const myInProgress = myProspects.filter(p => {
        const isWon = VALIDATED_EP_STAGES.includes(p.easy_power_stage) || VALIDATED_CH_STAGES.includes(p.chantier_stage);
        return !isWon && (p.easy_power_stage || p.chantier_stage);
    });
    
    // Calculer mes commissions
    const myTotalCommission = myWonProspects.reduce((sum, p) => 
        sum + (p.ep_commission_apporteur || 0) + (p.ch_commission_apporteur || 0), 0
    );
    
    // Taux de conversion
    const conversionRate = myProspects.length > 0 
        ? Math.round((myWonProspects.length / myProspects.length) * 100) 
        : 0;

    // Clic sur un KPI -> afficher EXACTEMENT la liste des dossiers qui composent le cadre
    const [selectedKpi, setSelectedKpi] = useState(null);
    const listRef = useRef(null);
    // Mes commissions PAR MODULE sur un dossier (Courtage / Travaux d’optimisation) — un dossier peut
    // porter les deux. Renvoie une entrée par module ACTIF (stage présent), avec son montant
    // (apporteur 1 OU 2 selon ma position) et son état "gagné".
    const moduleCommissions = (p) => makeModuleCommissions(p, user?.id);
    // Dossiers "à aller chercher" : au moins un module avec commission renseignée NON gagnée.
    const myAChercher = myProspects.filter((p) => moduleCommissions(p).some((e) => !e.won && e.amount > 0));
    const kpiConfig = {
        prospects: { label: 'Mes prospects', items: myProspects, showCommission: false },
        in_progress: { label: 'En cours', items: myInProgress, showCommission: false },
        won: { label: 'Gagnés', items: myWonProspects, showCommission: true },
        commission: { label: 'Ma commission', items: myWonProspects, showCommission: true },
        a_chercher: { label: 'À aller chercher', items: myAChercher, showCommission: true },
    };
    const toggleKpi = (k) => {
        setSelectedKpi((cur) => (cur === k ? null : k));
        setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    };
    const selected = selectedKpi ? kpiConfig[selectedKpi] : null;

    // « À aller chercher » : somme des commissions RENSEIGNÉES mais pas encore acquises (EP+CH
    // par module) + nombre de dossiers en cours SANS commission encore (à faire avancer).
    let aChercherTotal = 0;
    let aFaireAvancerCount = 0;
    myProspects.forEach((p) => {
        let hasToAdvance = false;
        moduleCommissions(p).forEach((e) => {
            if (e.won) return;
            if (e.amount > 0) aChercherTotal += e.amount;
            else hasToAdvance = true;
        });
        if (hasToAdvance) aFaireAvancerCount += 1;
    });

    return (
        <div className="space-y-6" data-testid="apporteur-dashboard">
            {/* Message de bienvenue personnalisé */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-green-100">
                            <Trophy className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-green-900">
                                Mes Performances
                            </h2>
                            <p className="text-green-700">
                                Suivez vos résultats et votre progression
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Objectif concret : commissions à aller chercher (pousse à closer) — cliquable */}
            <Card role="button" tabIndex={0} data-testid="apporteur-a-chercher-card"
                onClick={() => toggleKpi('a_chercher')}
                className={cn('bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5',
                    selectedKpi === 'a_chercher' && 'ring-2 ring-amber-400')}>
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-amber-100 shrink-0">
                            <Target className="w-8 h-8 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">À aller chercher</p>
                            <p className="text-3xl font-bold text-amber-800" data-testid="apporteur-a-chercher-amount">
                                {formatCurrency(aChercherTotal)}
                            </p>
                            <p className="text-sm text-amber-800/90 mt-0.5">
                                {aChercherTotal > 0
                                    ? "À portée de main : closez vos dossiers pour l'encaisser."
                                    : 'Faites avancer vos dossiers pour débloquer vos commissions.'}
                                {aFaireAvancerCount > 0 && ` Et ${aFaireAvancerCount} dossier${aFaireAvancerCount > 1 ? 's' : ''} en cours à faire avancer pour voir la commission.`}
                            </p>
                            {aChercherTotal > 0 && (
                                <p className="text-xs font-medium text-amber-700 mt-1 inline-flex items-center gap-1">
                                    <ArrowRight className="w-3.5 h-3.5" /> Voir les dossiers à closer
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats principales — cliquables pour voir les dossiers concernés */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-prospects"
                    onClick={() => toggleKpi('prospects')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5', selectedKpi === 'prospects' && 'ring-2 ring-blue-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Mes prospects</p>
                                <p className="text-3xl font-bold text-slate-900">{myProspects.length}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-in-progress"
                    onClick={() => toggleKpi('in_progress')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5', selectedKpi === 'in_progress' && 'ring-2 ring-amber-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">En cours</p>
                                <p className="text-3xl font-bold text-amber-600">{myInProgress.length}</p>
                            </div>
                            <Clock className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-won"
                    onClick={() => toggleKpi('won')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-green-200 bg-green-50/30', selectedKpi === 'won' && 'ring-2 ring-green-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{myWonProspects.length}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-commission"
                    onClick={() => toggleKpi('commission')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-emerald-200 bg-emerald-50/30', selectedKpi === 'commission' && 'ring-2 ring-emerald-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-emerald-600">Ma commission</p>
                                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(myTotalCommission)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-emerald-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Liste des dossiers qui composent le cadre sélectionné */}
            {selected && (
                <Card ref={listRef} data-testid="apporteur-kpi-list" className="border-slate-200">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Activity className="w-5 h-5 text-slate-600" />
                                {selected.label} — {selected.items.length} dossier{selected.items.length > 1 ? 's' : ''}
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedKpi(null)} data-testid="apporteur-kpi-list-close">Fermer</Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {selected.items.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">Aucun dossier dans cette catégorie.</p>
                        ) : (
                            <div className="divide-y">
                                {selected.items.map((p) => (
                                    <div key={p.id}
                                        className="flex items-center justify-between gap-3 py-3 cursor-pointer hover:bg-slate-50 -mx-4 px-4"
                                        onClick={() => navigate(`/prospects/${p.id}`)}
                                        data-testid={`apporteur-list-row-${p.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-medium truncate">{p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}</p>
                                            <p className="text-sm text-slate-500 truncate">{p.first_name} {p.last_name}</p>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <Badge variant="outline" className="shrink-0">{p.easy_power_stage || p.chantier_stage || 'En cours'}</Badge>
                                            <CommissionCells entries={moduleCommissions(p)} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* Taux de conversion */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-600" />
                        Mon taux de conversion
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Prospects → Gagnés</span>
                            <span className="text-lg font-bold text-blue-600">{conversionRate}%</span>
                        </div>
                        <Progress value={conversionRate} className="h-3" />
                        <p className="text-xs text-slate-500">
                            {myWonProspects.length} dossiers gagnés sur {myProspects.length} prospects
                        </p>
                    </div>
                </CardContent>
            </Card>
            
            {/* Mes derniers prospects */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Activity className="w-5 h-5 text-slate-600" />
                            Mes derniers prospects
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/prospects')}>
                            Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {myProspects.slice(0, 5).map(prospect => (
                        <div 
                            key={prospect.id}
                            className="flex items-center justify-between py-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 -mx-4 px-4"
                            onClick={() => navigate(`/prospects/${prospect.id}`)}
                        >
                            <div>
                                <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
                                <p className="text-sm text-slate-500">{prospect.company_name || 'Particulier'}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                {prospect.easy_power_stage === 'ep_gagne' && (
                                    <Badge className="bg-green-100 text-green-700">Gagné EP</Badge>
                                )}
                                {prospect.chantier_stage === 'ch_valide' && (
                                    <Badge className="bg-blue-100 text-blue-700">Gagné CH</Badge>
                                )}
                                {!prospect.easy_power_stage?.includes('gagne') && !prospect.chantier_stage?.includes('valide') && (
                                    <Badge variant="outline">En cours</Badge>
                                )}
                                <CommissionCells entries={moduleCommissions(prospect)} />
                            </div>
                        </div>
                    ))}
                    {myProspects.length === 0 && (
                        <p className="text-center text-slate-500 py-8">
                            Aucun prospect pour le moment
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE VP PAYEUR - Mon équipe et mes apporteurs
// ============================================================
export function VPPayeurDashboardView({ user, prospects, teamMembers, teams, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Mes apporteurs
    const myApporteurs = teamMembers.filter(m => m.role === 'apporteur');
    
    // Stats de mon équipe
    const teamProspects = prospects.filter(p => {
        const teamIds = [user?.id, ...myApporteurs.map(a => a.id)];
        return teamIds.includes(p.assigned_to) || teamIds.includes(p.dossier_vp_id) || teamIds.includes(p.dossier_apporteur_id);
    });
    
    const teamWon = filterWonByPeriod(teamProspects, advancedFilters);
    
    // Ma commission totale
    const myCommissionBrute = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_vp_payeur_brute || 0) + (p.ch_commission_vp || 0), 0
    );
    const myCommissionNette = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_vp_payeur_nette || 0) + (p.ch_commission_vp || 0), 0
    );
    
    // Restant à payer aux apporteurs
    const restantApporteurs = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_apporteur || 0) + (p.ch_commission_apporteur || 0), 0
    );
    
    // Performance par apporteur
    const apporteurStats = myApporteurs.map(apporteur => {
        const apporteurProspects = teamProspects.filter(p => 
            p.dossier_apporteur_id === apporteur.id || p.assigned_to === apporteur.id
        );
        const won = filterWonByPeriod(apporteurProspects, advancedFilters);
        return {
            ...apporteur,
            prospects: apporteurProspects.length,
            won: won.length,
            commission: won.reduce((sum, p) => sum + (p.ep_commission_apporteur || 0), 0)
        };
    }).sort((a, b) => b.won - a.won);
    
    return (
        <div className="space-y-6" data-testid="vp-payeur-dashboard">
            {/* Message de bienvenue */}
            <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-purple-100">
                            <Crown className="w-8 h-8 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-purple-900">
                                Mon Équipe
                            </h2>
                            <p className="text-purple-700">
                                {myApporteurs.length} apporteur{myApporteurs.length > 1 ? 's' : ''} dans votre équipe
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Prime de secteur retirée d'ici — désormais affichée UNE SEULE FOIS
                au niveau du Dashboard (Dashboard.jsx L906) pour tous les VP. */}

            {/* Stats équipe */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Dossiers équipe</p>
                                <p className="text-3xl font-bold text-slate-900">{teamProspects.length}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{teamWon.length}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-purple-200 bg-purple-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-purple-600">Ma commission (nette)</p>
                                <p className="text-2xl font-bold text-purple-700">{formatCurrency(myCommissionNette)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-purple-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Commission Apporteurs</p>
                                <p className="text-2xl font-bold text-amber-700">{formatCurrency(restantApporteurs)}</p>
                            </div>
                            <Users className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Performance de mes apporteurs */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-600" />
                            Mes Apporteurs
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/teams')}>
                            Gérer <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {apporteurStats.map((apporteur, index) => (
                            <div 
                                key={apporteur.id}
                                className="flex items-center justify-between py-3 border-b last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-medium",
                                        index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-700" : "bg-slate-300"
                                    )}>
                                        {index < 3 ? <Medal className="w-4 h-4" /> : index + 1}
                                    </div>
                                    <div>
                                        <p className="font-medium">{apporteur.first_name} {apporteur.last_name}</p>
                                        <p className="text-xs text-slate-500">
                                            {apporteur.prospects} prospect{apporteur.prospects > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-green-600">{apporteur.won} gagné{apporteur.won > 1 ? 's' : ''}</p>
                                    <p className="text-xs text-slate-500">{formatCurrency(apporteur.commission)}</p>
                                </div>
                            </div>
                        ))}
                        {myApporteurs.length === 0 && (
                            <p className="text-center text-slate-500 py-8">
                                Aucun apporteur dans votre équipe
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE VP SUPERVISEUR - Toutes mes équipes et VP
// ============================================================
export function VPSuperviseurDashboardView({ user, prospects, teamMembers, teams, allUsers, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Mes équipes SUPERVISÉES UNIQUEMENT (STRICTEMENT dissociées de mon équipe personnelle
    // où je suis VP Payeur — celle-ci est affichée dans la vue "Mon Équipe" au-dessus).
    // On exclut aussi toute équipe où je serais à la fois payeur et superviseur (cas rare).
    const myTeams = teams.filter(t => 
        t.supervisor_vp_id === user?.id && t.payer_vp_id !== user?.id
    );
    
    // Mes VP Payeurs (ceux des équipes supervisées, jamais moi-même)
    const myVPs = allUsers?.filter(u => 
        (u.role === 'vp' || u.is_vp_effective) && u.id !== user?.id && myTeams.some(t => t.payer_vp_id === u.id)
    ) || [];
    
    // Stats calculées PAR équipe supervisée, sans mélanger avec mon équipe personnelle.
    // Pour chaque équipe : périmètre strict = payer_vp de l'équipe + apporteurs (members) de l'équipe.
    const teamStats = myTeams.map(team => {
        const teamMemberIds = new Set();
        if (team.payer_vp_id) teamMemberIds.add(team.payer_vp_id);
        (team.members || []).forEach(m => teamMemberIds.add(m.id));
        // Prospects strictement rattachés à CETTE équipe
        const teamProspects = prospects.filter(p => 
            teamMemberIds.has(p.assigned_to) ||
            teamMemberIds.has(p.dossier_vp_id) ||
            teamMemberIds.has(p.dossier_apporteur_id)
        );
        const won = filterWonByPeriod(teamProspects, advancedFilters);
        const commission = won.reduce((sum, p) => sum + (p.ep_commission_vp_superviseur || 0), 0);
        const payerVp = allUsers?.find(u => u.id === team.payer_vp_id);
        return {
            ...team,
            payer_vp_display_name: payerVp ? `${payerVp.first_name} ${payerVp.last_name}` : (team.payer_vp_name || 'Non assigné'),
            member_count: (team.members || []).length,
            prospects_count: teamProspects.length,
            won_count: won.length,
            commission,
        };
    }).sort((a, b) => b.won_count - a.won_count);
    
    // Totaux AGRÉGÉS UNIQUEMENT sur les équipes supervisées (pas d'équipe perso incluse)
    const totalWonCount = teamStats.reduce((s, t) => s + t.won_count, 0);
    const totalProspects = teamStats.reduce((s, t) => s + t.prospects_count, 0);
    const totalCommission = teamStats.reduce((s, t) => s + t.commission, 0);
    
    if (myTeams.length === 0) return null;
    
    return (
        <div className="space-y-6" data-testid="vp-superviseur-dashboard">
            {/* Message de bienvenue */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-amber-100">
                            <Star className="w-8 h-8 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold text-amber-900">
                                Vue Superviseur — Équipes supervisées
                            </h2>
                            <p className="text-amber-700">
                                {myTeams.length} équipe{myTeams.length > 1 ? 's' : ''} supervisée{myTeams.length > 1 ? 's' : ''} • {myVPs.length} VP Payeur{myVPs.length > 1 ? 's' : ''}
                            </p>
                            <p className="text-xs text-amber-600/80 mt-1">
                                Ces données sont dissociées de votre équipe personnelle (affichée séparément ci-dessus).
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats globales des équipes SUPERVISÉES uniquement */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Équipes supervisées</p>
                                <p className="text-3xl font-bold text-slate-900">{myTeams.length}</p>
                            </div>
                            <Users className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Dossiers supervisés</p>
                                <p className="text-3xl font-bold text-slate-900">{totalProspects}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-slate-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{totalWonCount}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Ma commission superviseur</p>
                                <p className="text-2xl font-bold text-amber-700">{formatCurrency(totalCommission)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Détail PAR équipe supervisée (chaque équipe = un bloc distinct et CLIQUABLE
                pour ouvrir le Pilotage scopé sur cette équipe = même vue qu'un VP Payeur
                a de son équipe : liste des dossiers, KPIs, actions à mener). */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-600" />
                            Performance par Équipe supervisée
                        </CardTitle>
                        <span className="text-xs text-slate-500 italic">Cliquez sur une équipe pour ouvrir son tableau de bord</span>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {teamStats.map((team, index) => (
                            <button
                                type="button"
                                key={team.id}
                                onClick={() => navigate(`/pilotage?scope_type=team&scope_id=${encodeURIComponent(team.id)}&scope_name=${encodeURIComponent(team.name)}`)}
                                className="w-full text-left p-4 rounded-lg border bg-slate-50/50 hover:bg-amber-50/60 hover:border-amber-300 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 group"
                                data-testid={`vp-sup-team-block-${team.id}`}
                                aria-label={`Ouvrir le tableau de bord de l'équipe ${team.name}`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                                            index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : "bg-slate-300"
                                        )}>
                                            {index + 1}
                                        </div>
                                        <div>
                                            <p className="font-semibold flex items-center gap-2" data-testid={`vp-sup-team-name-${team.id}`}>
                                                {team.name}
                                                <ArrowRight className="w-3.5 h-3.5 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                VP Payeur : {team.payer_vp_display_name} • {team.member_count} apporteur{team.member_count > 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-green-600">{team.won_count} gagné{team.won_count > 1 ? 's' : ''}</p>
                                        <p className="text-xs text-slate-500">{team.prospects_count} prospects • {formatCurrency(team.commission)}</p>
                                    </div>
                                </div>
                                <Progress
                                    value={team.prospects_count > 0 ? (team.won_count / team.prospects_count) * 100 : 0}
                                    className="h-2"
                                />
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE ADMIN - Vue globale complète
// ============================================================
export function AdminDashboardView({ prospects, allUsers, teams, commissions, dashboardStats, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Utiliser les stats pré-calculées du Dashboard (filtrées par période)
    const totalProspects = dashboardStats?.totalProspects ?? prospects.length;
    const wonProspects = dashboardStats?.prospectsGagnes || filterWonByPeriod(prospects, advancedFilters);
    const validatedCount = dashboardStats?.validatedCount ?? wonProspects.length;
    
    // Stats par rôle
    const vpCount = allUsers?.filter(u => (u.role === 'vp' || u.is_vp_effective) && u.is_active).length || 0;
    const apporteurCount = allUsers?.filter(u => u.role === 'apporteur' && u.is_active).length || 0;
    
    // Commission Valnexo sur les dossiers gagnés (filtrés par période)
    const totalValnexoCommission = dashboardStats?.commissionsValidees ?? wonProspects.reduce((sum, p) => 
        sum + (p.ep_commission_valnexo || 0) + (p.ch_commission_valnexo || 0), 0
    );
    
    // Top performers - filtré par la même période
    const vpPerformance = allUsers?.filter(u => u.role === 'vp' || u.is_vp_effective).map(vp => {
        const vpProspects = prospects.filter(p => p.dossier_vp_id === vp.id);
        const won = wonProspects.filter(p => p.dossier_vp_id === vp.id);
        return {
            ...vp,
            prospects: vpProspects.length,
            won: won.length
        };
    }).sort((a, b) => b.won - a.won).slice(0, 5) || [];
    
    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            {/* Stats globales */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Total dossiers</p>
                                <p className="text-3xl font-bold text-slate-900">{totalProspects}</p>
                                <p className="text-xs text-slate-400 mt-1">Dossiers {periodLabel}</p>
                            </div>
                            <Briefcase className="w-8 h-8 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{validatedCount}</p>
                                <p className="text-xs text-green-500 mt-1">Gagnés {periodLabel}</p>
                            </div>
                            <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">VP actifs</p>
                                <p className="text-3xl font-bold text-purple-600">{vpCount}</p>
                            </div>
                            <Crown className="w-8 h-8 text-purple-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Apporteurs actifs</p>
                                <p className="text-3xl font-bold text-emerald-600">{apporteurCount}</p>
                            </div>
                            <Users className="w-8 h-8 text-emerald-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Commission Valnexo</p>
                                <p className="text-xl font-bold text-amber-700">{formatCurrency(totalValnexoCommission)}</p>
                                <p className="text-xs text-amber-500 mt-1">Commission {periodLabel}</p>
                            </div>
                            <Euro className="w-8 h-8 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Top VP */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-600" />
                            Top VP
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
                            Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {vpPerformance.map((vp, index) => (
                            <div 
                                key={vp.id}
                                className="flex items-center justify-between py-2 border-b last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                                        index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-700" : "bg-slate-300"
                                    )}>
                                        {index < 3 ? <Medal className="w-4 h-4" /> : index + 1}
                                    </div>
                                    <div>
                                        <p className="font-medium">{vp.first_name} {vp.last_name}</p>
                                        <Badge className="text-xs bg-purple-100 text-purple-700">VP</Badge>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-green-600">{vp.won} gagné{vp.won > 1 ? 's' : ''}</p>
                                    <p className="text-xs text-slate-500">{vp.prospects} prospects</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default {
    ApporteurDashboardView,
    VPPayeurDashboardView,
    VPSuperviseurDashboardView,
    AdminDashboardView
};
